// IconButton (DESIGN §3.6.2): rund 40 (sm 32, xs 28, lg 44), Icon 20 (sm/xs 16), Pflicht-`label`
// → aria-label + Tooltip. Toggle über `pressed` (aria-pressed, Fläche accent-soft).
import type { ButtonHTMLAttributes, MouseEvent, ReactNode, Ref } from 'react';
import type { Surface } from './Button';
import { Tooltip } from './Tooltip';
import { cn, cva, Spinner } from '../basis/kern';

export const iconButtonVariants = cva(
  [
    'relative inline-flex shrink-0 items-center justify-center rounded-full select-none',
    'transition-[background-color,color,transform] duration-(--bar-dur-fast) ease-standard',
    'active:scale-[.97] disabled:cursor-not-allowed disabled:text-fg-disabled disabled:active:scale-100',
    'aria-disabled:cursor-not-allowed aria-disabled:text-fg-disabled',
  ],
  {
    variants: {
      variant: {
        secondary: 'text-fg',
        ghost: 'bg-transparent text-fg',
        primary:
          'bg-primary text-on-primary hover:not-disabled:bg-primary-hover disabled:bg-pill-card disabled:text-fg-disabled',
        'danger-ghost': 'bg-transparent text-danger-fg hover:not-disabled:bg-danger-soft',
      },
      surface: { page: '', card: '', stripe: '', tile: '' },
      size: { xs: 'size-7', sm: 'size-8', md: 'size-10', lg: 'h-10 w-11' },
      shape: { round: '', pill: 'w-[46px]' },
      pressed: { true: 'bg-accent-soft! text-accent-fg!', false: '' },
    },
    compoundVariants: [
      {
        variant: 'secondary',
        surface: 'page',
        class: 'bg-pill-page hover:not-disabled:bg-pill-page-hover',
      },
      {
        variant: 'secondary',
        surface: 'card',
        class: 'bg-pill-card hover:not-disabled:bg-pill-card-hover',
      },
      {
        variant: 'secondary',
        surface: 'stripe',
        class: 'bg-pill-stripe hover:not-disabled:bg-pill-stripe-hover',
      },
      {
        variant: 'secondary',
        surface: 'tile',
        class: 'bg-pill-tile hover:not-disabled:bg-pill-tile-hover',
      },
      { variant: 'ghost', surface: 'page', class: 'hover:not-disabled:bg-pill-page' },
      { variant: 'ghost', surface: 'card', class: 'hover:not-disabled:bg-pill-card' },
      { variant: 'ghost', surface: 'stripe', class: 'hover:not-disabled:bg-pill-stripe' },
      { variant: 'ghost', surface: 'tile', class: 'hover:not-disabled:bg-pill-tile' },
      { size: 'lg', shape: 'round', class: 'size-11' },
    ],
    defaultVariants: {
      variant: 'secondary',
      surface: 'page',
      size: 'md',
      shape: 'round',
      pressed: false,
    },
  },
);

export interface IconButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children' | 'aria-label'
> {
  ref?: Ref<HTMLButtonElement>;
  /** Pflicht: Name für Screenreader und Tooltip. */
  label: string;
  icon: ReactNode;
  variant?: 'secondary' | 'ghost' | 'primary' | 'danger-ghost';
  surface?: Surface;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /** `pill` = „…“-Pill 46 × 40 (DESIGN §3.6.2). */
  shape?: 'round' | 'pill';
  /** Toggle-Zustand (Pin, Daumen); setzt aria-pressed. */
  pressed?: boolean;
  loading?: boolean;
  /** Tooltip aus (z. B. wenn ein sichtbares Label daneben steht). Standard: an. */
  tooltip?: boolean;
  tooltipSide?: 'top' | 'right' | 'bottom' | 'left';
  shortcut?: string;
  /** Zähler-Badge oben rechts (z. B. Benachrichtigungen); der Name kommt über `label`. */
  badge?: ReactNode;
}

export function IconButton({
  label,
  icon,
  variant = 'secondary',
  surface = 'page',
  size = 'md',
  shape = 'round',
  pressed,
  loading = false,
  tooltip = true,
  tooltipSide,
  shortcut,
  badge,
  type = 'button',
  className,
  onClick,
  ...rest
}: IconButtonProps) {
  const iconSize = size === 'xs' || size === 'sm' ? 16 : 20;
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (loading || rest['aria-disabled'] === true || rest['aria-disabled'] === 'true') {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  };
  const button = (
    <button
      type={type}
      {...rest}
      aria-label={label}
      aria-pressed={pressed === undefined ? undefined : pressed}
      aria-busy={loading || undefined}
      data-variant={variant}
      data-size={size}
      data-state={pressed === undefined ? undefined : pressed ? 'on' : 'off'}
      onClick={handleClick}
      className={cn(
        iconButtonVariants({ variant, surface, size, shape, pressed: pressed ?? false }),
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'inline-flex [&>svg]:shrink-0',
          iconSize === 16 ? '[&>svg]:size-4' : '[&>svg]:size-5',
        )}
      >
        {loading ? <Spinner size={iconSize} /> : icon}
      </span>
      {badge ? <span className="absolute -top-1 -right-1">{badge}</span> : null}
    </button>
  );
  return (
    <Tooltip content={label} side={tooltipSide} shortcut={shortcut} disabled={!tooltip}>
      {button}
    </Tooltip>
  );
}
